// MIDAS V3 — the per-block text pipeline of the streaming surface
// (StreamingMarkdownV3), lifted out of the component so it is unit-testable:
// react-markdown is ESM-only and cannot be loaded by this Jest setup.
//
// One TEXT block of the in-flight buffer → the string handed to ReactMarkdown:
//   1. r5c — the bot's raw `[[live-row]]` / `[[src:…]]` shorthand (the deltas
//      carry it; the elements arrive only in the final `answer` event): hold a
//      half one back, expand complete mint-exact ones into the void tags they
//      are minted into (srcChipsGrammar).
//      MIDAS-STREAM-SHORTHAND-FAMILIES-1 does the same for the other families
//      (streamChipShorthandGrammar): `[[live:…]]` / `[[plc:…]]` / `[[tel:…]]`
//      become the tag the bot mints, `[[ref:…]]` is dropped as the final
//      formatter drops it, and a half `[[list:` / `[[page:` is held too.
//   2. chip hold-back (Task 2 / TEL_REF_RENDERER_SPEC §6A.2) —
//      trimUnclosedRefTagTail holds a half-open paired chip tag (opening- OR
//      closing-tag split, §6A.7); trimUnclosedVoidTagTail (r5) does the same
//      for the self-closing <live-row/> / <src-ref/>.
//   3. rewriteAllRefsToSentinels collapses every COMPLETE chip tag (and the
//      `[[list:N]]` / `[[page:N]]` shorthand it owns) to a sentinel. A block
//      with neither `<` nor `[[` is plain prose and skips the ~8 regex passes
//      (§6A.4).
// We deliberately do NOT add preprocessMarkdown here (mid-stream bullet
// re-stitching would reflow paragraphs on every token); the finalized message
// applies it once per block.
import { trimUnclosedRefTagTail } from "../../../../shared/chatBot/streamBotMessage";
import { rewriteAllRefsToSentinels } from "./markdownComponentsV3";
import {
  expandStreamSrcShorthand,
  holdPartialSrcShorthandTail,
  trimUnclosedVoidTagTail,
} from "./srcChipsGrammar";
import {
  expandStreamChipShorthand,
  holdPartialChipShorthandTail,
} from "./streamChipShorthandGrammar";

// The chip tags whose half-open tails the streaming trimmer holds back AND that
// rewriteAllRefsToSentinels collapses to chips mid-stream. page-ref is included
// for symmetry — it is part of rewriteAllRefsToSentinels (markdownComponentsV3
// §5 step 4), so a streamed page-ref also renders live and is tail-trimmed.
// comp-ref (DIAG-OVERLAY-B) is listed for the SAME symmetry reason: the bot
// contract says component chips appear only in the final `answer` event, never
// in deltas — but if a comp-ref ever DID stream, omitting it here would flash
// `<comp-ref compo` as raw prose. Listing it costs one array entry.
// (Chips rendered on the streaming surface are non-interactive: the click
// delegate lives on the final-message container, not here.)
// <live-row/> / <src-ref/> are VOID tags — they can't go here (this trimmer
// waits for a `</tag>` that never comes); trimUnclosedVoidTagTail covers them.
export const STREAMING_CHIP_TAGS = [
  "list-ref",
  "tel-ref",
  "live-ref",
  "plc-ref",
  "page-ref",
  "comp-ref",
  "val",
];

export const prepareStreamingText = (text: string): string => {
  const held = holdPartialChipShorthandTail(holdPartialSrcShorthandTail(text));
  const shorthandSafe = expandStreamChipShorthand(expandStreamSrcShorthand(held));
  const chipSafe = trimUnclosedVoidTagTail(
    trimUnclosedRefTagTail(shorthandSafe, STREAMING_CHIP_TAGS).safe
  );
  return chipSafe.indexOf("<") === -1 && chipSafe.indexOf("[[") === -1
    ? chipSafe
    : rewriteAllRefsToSentinels(chipSafe);
};
